import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { images } from "@img/imagesMap";
import * as mutate from "@script/mutate";
import "@css/comp_table.css";
import "@css/temp_headNurse_management.css";
import MgmtExcelUpload from "@components/feature/head_nurse_mgmt/MgmtExcelUpload";
import AddGroupModal from "@components/feature/head_nurse_mgmt/AddGroupModal";
import MgmtExcelUpload2 from "@components/feature/head_nurse_mgmt/MgmtExcelUpload2";
import GradeSettingModal from "@components/feature/head_nurse_mgmt/GradeSettingModal";
import useQueryInvaildate from "@hooks/useQueryInvaildate";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { NurseResponse } from "@vTypes/nurse-type";
import type { FullNurseSchema } from "@vTypes";
import useStore from "@store/store";
import { useGroupSelection } from "@hooks/useGroupSelection";
import ActionButton from "@components/ui/button/btn-action.tsx";
import NurseEditSideProfile from "@components/feature/head_nurse_mgmt/NurseEditSideProfile";
import { toast } from "@/components/toast/CustomToast";
import type { GroupMembersResponse } from "@/api/nurses";

import {
  type TeamsSchema,
} from "@/hooks/roster-craete/useTeamsQuery";
import type { GradeConfigQueryResult } from "@/hooks/useGradeConfig";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import type { RosterConfigObject } from "@/hooks/useRosterConfig";
import { usePreceptorMapping } from "@/hooks/usePreceptorMapping";
import NurseManagementTables from "./components/NurseManagementTables";
import NurseActiveSwitch from "@/components/feature/head_nurse_mgmt/NurseActiveSwitch";
import NurseAddModal from "@/components/feature/head_nurse_mgmt/NurseAddModal";
import TeamSettingModal from "@/components/feature/head_nurse_mgmt/TeamSettingModal";
import WardRedistributeModal from "@/components/feature/head_nurse_mgmt/WardRedistributeModal";
import { useSortableNurses } from "@/hooks/useSortableNurses";
import { useNurseSequenceReorder } from "@/hooks/useNurseSequenceReorder";
import { linkPreceptorRelations } from "@/utils/nurse-util";
import { applyPrecepteePeriodsToNurses } from "@/utils/precepteePolicy";
import { sortNurseListByRoleThenSequence } from "@/utils/common-util";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ViewModeSelector from "@/components/ui/ViewModeSelector";
import type { PrecepteePeriodItem } from "@/hooks/roster-craete/usePrecepteePeriodsQuery";

interface HeadNurseManagementProps {
  selectedYear: number;
  selectedMonth: number;
  nurseDatas?: NurseResponse[];
  precepteePeriods?: PrecepteePeriodItem[];
  teamsSchema?: TeamsSchema;
  gradeData?: GradeConfigQueryResult;
  shiftsData?: ShiftReturnType;
  rosterConfig?: RosterConfigObject;
  monthMembers?: GroupMembersResponse;
  isMonthMembersFetching: boolean;
  onShiftMonth: (delta: number) => void;
}

function useCompletedMonthTransition(
  signal: string,
  isPending: boolean
): boolean {
  const mountedRef = useRef(false);
  const completedSignalRef = useRef(signal);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      completedSignalRef.current = signal;
      return;
    }

    if (isPending || completedSignalRef.current === signal) return;

    completedSignalRef.current = signal;
    setIsTransitioning(false);
    const frame = window.requestAnimationFrame(() => {
      setIsTransitioning(true);
    });
    const timeout = window.setTimeout(() => {
      setIsTransitioning(false);
    }, 420);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [isPending, signal]);

  return isTransitioning;
}
function Head_nurse_management({
  selectedYear,
  selectedMonth,
  nurseDatas,
  precepteePeriods,
  teamsSchema,
  gradeData,
  shiftsData,
  rosterConfig,
  monthMembers,
  isMonthMembersFetching,
  onShiftMonth,
}: HeadNurseManagementProps) {
  const { groups, fetchGroups, selectedGroupId, setSelectedGroupId, isAdmin } =
    useGroupSelection();
  const { invalidate } = useQueryInvaildate();
  const { loginUser } = useStore();
  const loginUserIsMasterAdmin = loginUser?.is_master_admin; // 어드민만

  const isNoGroupAdmin = useMemo(
    () => isAdmin && Array.isArray(groups) && groups.length === 0,
    [isAdmin, groups]
  );
  const targetGroupId = isAdmin ? selectedGroupId || undefined : undefined;
  const selectedGroupName = useMemo(
    () => groups.find((group) => group.group_id === selectedGroupId)?.group_name,
    [groups, selectedGroupId]
  );

  // [Phase4] 근무자관리 월 셀렉터 — 선택 월 '소속' 멤버 + 헤드카운트(소속 기준).
  //   월별 소속 조회는 페이지에서 관리해 전환 로딩 오버레이와 함께 제어한다.
  const headcount = monthMembers?.headcount;
  // [Phase4 B-1] nurse_id→선택 월 소속 상태 (행 배지 오버레이용). 없으면 배지 미표시.
  const memberStatusMap = useMemo(
    () =>
      new Map(
        (monthMembers?.members ?? []).map((m) => [m.nurse_id, m] as const)
      ),
    [monthMembers]
  );
  const monthTransitionSignal = `${selectedYear}-${selectedMonth}`;
  const isMonthSectionTransitioning = useCompletedMonthTransition(
    monthTransitionSignal,
    isMonthMembersFetching
  );
  const nursesForSorting = useMemo(
    () =>
      linkPreceptorRelations(
        sortNurseListByRoleThenSequence(
          applyPrecepteePeriodsToNurses(nurseDatas, precepteePeriods)
        )
      ),
    [nurseDatas, precepteePeriods]
  );
  const nurseDatasWithPrecepteePeriods = useMemo(
    () => applyPrecepteePeriodsToNurses(nurseDatas, precepteePeriods),
    [nurseDatas, precepteePeriods]
  );

  // 정렬 상태를 하나로 통합 관리 (활성/비활성 공통)
  const {
    sortedNurses,
    sortConfig,
    handleSort,
    resetSort,
  } = useSortableNurses(nursesForSorting, {
    enableSequenceUpdate: true, // 활성 근무자는 서버에 저장
    keepRoleGroupOrder: true, // RN → AN → ETC 그룹을 유지한 채 내부 정렬
    groupId: targetGroupId ?? null,
  });
  const { moveNurse } = useNurseSequenceReorder({
    nurses: sortedNurses,
    selectedGroupId: targetGroupId ?? null,
    listGroupId: targetGroupId ?? null,
    fullSchemaGroupId: targetGroupId ?? null,
    onManualSortReset: resetSort,
  });

  // 낙관적 업데이트를 위한 로컬 상태 (부모로 끌어올림)
  const [localTeams, setLocalTeams] = useState<TeamsSchema>(teamsSchema!);

  // 서버 데이터 동기화
  useEffect(() => {
    if (teamsSchema) setLocalTeams(teamsSchema);
  }, [teamsSchema]);

  const [isTeamModalOpen, setIsTeamModalOpen] = useState<boolean>(false);
  const [isRedistributeOpen, setIsRedistributeOpen] = useState<boolean>(false);
  const [isNurseModalOpen, setIsNurseModalOpen] = useState<boolean>(false);
  const [isNurseAddModalOpen, setIsNurseAddModalOpen] =
    useState<boolean>(false);
  const [viewState, setViewState] = useState<"ALL" | "TEAM">("ALL");

  const inactiveNurseSchema = useMemo(() => {
    const inactiveList: NurseResponse[] = [];

    nurseDatasWithPrecepteePeriods.forEach((nurse) => {
      if (nurse.active === 0) {
        inactiveList.push(nurse);
      }
    });

    return inactiveList;
  }, [nurseDatasWithPrecepteePeriods]);

  // [Phase4] 상단 토탈/활성 카운트는 as-of(선택 월 소속) 기준으로 통일한다.
  //   정규/이동/휴직(headcount)·비활성 합과 어긋나지 않도록,
  //   캐시(useNurses) 길이 대신 선택 월 소속 명단(monthMembers.members) 길이를 쓴다.
  //   비활성은 active=0 집합이라 전출과 무관해 캐시 기준(inactiveNurseSchema)을 그대로 합산.
  const asOfActiveCount = monthMembers?.members?.length ?? 0;
  const inactiveCount = inactiveNurseSchema?.length ?? 0;
  const totalMemberCount = asOfActiveCount + inactiveCount;

  const [isOpendExcelUpload, setIsOpendExcelUpload] = useState<boolean>(false);
  const [isOpendExcelUpload2, setIsOpendExcelUpload2] =
    useState<boolean>(false);

  const [isDeleteAllNursesButtonClick, setIsDeleteAllNursesButtonClick] =
    useState<boolean>(false);

  const [openAddGroup, setOpenAddGroup] = useState(false);

  const [isSideProfileOpend, setIsSideProfileOpend] = useState<boolean>(false);
  const [activeNurseIdForSideProfile, setActiveNurseIdForSideProfile] =
    useState<string | null>(null);
  const currentMgmtGroupId = isAdmin
    ? (selectedGroupId ?? null)
    : (loginUser?.group_id ?? null);
  const activeNurseForSideProfile = useMemo(
    () =>
      nurseDatasWithPrecepteePeriods.find(
        (n) => n.nurse_id === activeNurseIdForSideProfile
      ) ?? null,
    [nurseDatasWithPrecepteePeriods, activeNurseIdForSideProfile]
  );
  const activeMemberForSideProfile = activeNurseIdForSideProfile
    ? memberStatusMap.get(activeNurseIdForSideProfile)
    : undefined;
  const isExternalNurseForSideProfile = useMemo(() => {
    if (activeMemberForSideProfile?.membership_status === "inbound") {
      return true;
    }
    if (!activeNurseForSideProfile || !currentMgmtGroupId) return false;
    return activeNurseForSideProfile.group_id !== currentMgmtGroupId;
  }, [
    activeMemberForSideProfile?.membership_status,
    activeNurseForSideProfile,
    currentMgmtGroupId,
  ]);

  // 현재팀 활성화 (ID로 관리)
  const [currentTeamId, setCurrentTeamId] = useState<number | null>(null);

  /* //근무자 그룹 내 팀 */
  const [isAddNewNurse, setIsAddNewNurse] = useState<boolean>(false);

  // Grade 설정 모달 상태
  const [isGradeModalOpen, setIsGradeModalOpen] = useState<boolean>(false);

  // 인스턴스 초기화
  const fetchNurseSchema = async (groupId?: string | null) => {
    const targetGroup = (groupId || selectedGroupId) ?? null;
    await invalidate([...QUERY_KEYS.nurses.listScope(targetGroup)]);
    await invalidate(["nurses", "fullSchema"]);
  };

  const { preceptorList } = usePreceptorMapping(nurseDatasWithPrecepteePeriods);

  async function deleteAllNurses() {
    if (confirm(`근무자를 전부 삭제하시겠습니까? (본인 제외)`)) {
      setIsDeleteAllNursesButtonClick(true);
      const deleteArray = nurseDatas!.filter(
        (n) => n.nurse_id === loginUser!.nurse_id
      );
      try {
        await mutate.saveChangeFullNurseSchema(
          deleteArray,
          isAdmin ? selectedGroupId : undefined,
          loginUser?.office_id ?? null
        );
        alert("삭제되었습니다.");
        fetchNurseSchema(isAdmin ? (selectedGroupId ?? undefined) : undefined);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "근무자 삭제가 실패했습니다."
        );
      } finally {
        setIsDeleteAllNursesButtonClick(false);
      }
    }
  }

  // SideProfile On off
  const selectIdForSideprofile = useCallback((bool: boolean, nurseID: string) => {
    setActiveNurseIdForSideProfile(bool ? null : nurseID);
    setIsSideProfileOpend(!bool);
  }, []);


  return (
    <div className="pb-20 w-full ">
      <div className="flex flex-col gap-2 items-center px-10 w-full">
        <div className="flex w-full min-w-0 flex-col">
          <div className="flex justify-between">
            <h2 className="font-pretendard text-[24px] font-bold text-sig  leading-relaxed">
              근무자 관리
            </h2>
          </div>

          <div className="mb-6 mt-5 flex w-full flex-wrap items-start justify-between gap-x-8 gap-y-4">
            {/* 전체인원 */}
            <div className="flex min-w-[420px] flex-1 basis-[520px] flex-wrap items-center gap-x-3.5 gap-y-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <img
                        src={images.icon_person_black}
                        alt=""
                        className="mr-[3px]"
                      />
                      <span className="text-sm mt-1 font-semibold">
                        {totalMemberCount}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      활성 {asOfActiveCount}명 / 비활성 {inactiveCount}명
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* 전체 팀별 셀렉터 */}
              <ViewModeSelector
                options={[
                  { value: "ALL", label: "전체보기" },
                  { value: "TEAM", label: "팀별보기" },
                ]}
                defaultValue={viewState}
                onValueChange={(value) => setViewState(value as "TEAM" | "ALL")}
              />

              {/* [Phase4] 월 셀렉터 — 선택 월 '소속' 기준 헤드카운트 */}
              <div className="ml-1 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onShiftMonth(-1)}
                  className="px-2 py-0.5 rounded text-sig font-semibold hover:bg-gray-100"
                  aria-label="이전 달"
                >
                  ‹
                </button>
                <span className="text-sm font-semibold min-w-[84px] text-center">
                  {selectedYear}년 {selectedMonth}월
                </span>
                <button
                  type="button"
                  onClick={() => onShiftMonth(1)}
                  className="px-2 py-0.5 rounded text-sig font-semibold hover:bg-gray-100"
                  aria-label="다음 달"
                >
                  ›
                </button>
              </div>
              {headcount && (
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  정규 {headcount.regular} · 이동 {headcount.moving} · 휴직{" "}
                  {headcount.leave}
                </span>
              )}
            </div>

            <div className="ml-auto flex min-w-[680px] flex-1 basis-[720px] flex-wrap items-center justify-end gap-2.5 max-[1535px]:min-w-0 max-[1535px]:basis-full max-[1535px]:justify-start [&>button]:min-h-11 [&>button]:gap-x-2.5 [&>button]:pl-2 [&>button]:pr-4 [&>button>div]:size-7 [&>button>span]:text-[13px]">
              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsNurseAddModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                근무자 추가
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsNurseModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                비활성 근무자 설정
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsGradeModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                Grade 설정
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsTeamModalOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                팀 설정
              </ActionButton>

              <ActionButton
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="ward redistribute"
                    className={`w-4 h-4 transition-all ${!isNoGroupAdmin && "group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"}`}
                  />
                }
                onClick={() => {
                  setIsRedistributeOpen(true);
                }}
                disabled={isNoGroupAdmin}
              >
                병동 재분배
              </ActionButton>

              <ActionButton
                icon={"+"}
                disabled={isAddNewNurse || isNoGroupAdmin}
                onClick={() => {
                  setIsOpendExcelUpload(false);
                  setIsOpendExcelUpload2(true);
                }}
              >
                근무자 엑셀 업로드
              </ActionButton>
              {loginUserIsMasterAdmin && (
                <ActionButton
                  icon={"-"}
                  disabled={isDeleteAllNursesButtonClick || isNoGroupAdmin}
                  onClick={deleteAllNurses}
                >
                  전체 삭제
                </ActionButton>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <style>
              {`
                @keyframes roster-list-refresh {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
              `}
            </style>
            <div className="flex flex-col">
              <NurseManagementTables
                localTeams={localTeams}
                setLocalTeams={setLocalTeams}
                currentTeamId={currentTeamId}
                setCurrentTeamId={setCurrentTeamId}
                selectedGroupId={targetGroupId ?? null}
                sortedNurses={sortedNurses}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                showOnlyActive={true}
                isAddNewNurse={isAddNewNurse}
                setIsAddNewNurse={setIsAddNewNurse}
                viewState={viewState}
                sortConfig={sortConfig}
                onSort={handleSort}
                memberStatusMap={memberStatusMap}
                transitionSignal={monthTransitionSignal}
                isTransitionPending={isMonthMembersFetching}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                nurseCacheData={nurseDatasWithPrecepteePeriods}
                gradeData={gradeData}
                shiftsData={shiftsData}
                isUseMid={rosterConfig?.use_mid ?? false}
                groupName={selectedGroupName}
                onMoveNurse={moveNurse}
              />
            </div>

            {/* 비활성 */}
            <div className="flex flex-col">
              <h3
                className="px-10 text-lg font-bold text-white bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3 will-change-transform"
                style={
                  isMonthSectionTransitioning
                    ? {
                        animation:
                          "roster-list-refresh 360ms ease-out",
                      }
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  <img src={images.icon_person} alt="" className="size-3 " />
                  {inactiveNurseSchema?.length ?? 0}
                </div>
                <div>비활성 근무자</div>
                <div></div>
              </h3>
              <NurseManagementTables
                localTeams={localTeams}
                setLocalTeams={setLocalTeams}
                currentTeamId={currentTeamId}
                setCurrentTeamId={setCurrentTeamId}
                selectedGroupId={targetGroupId ?? null}
                sortedNurses={sortedNurses}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                showOnlyActive={false}
                viewState={viewState}
                sortConfig={sortConfig}
                onSort={handleSort}
                transitionSignal={monthTransitionSignal}
                isTransitionPending={isMonthMembersFetching}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                nurseCacheData={nurseDatasWithPrecepteePeriods}
                gradeData={gradeData}
                shiftsData={shiftsData}
                isUseMid={rosterConfig?.use_mid ?? false}
                groupName={selectedGroupName}
                onMoveNurse={moveNurse}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 엑셀 업로드 */}
      {preceptorList && isOpendExcelUpload && (
        <MgmtExcelUpload
          fullNurseSchema={(nurseDatas ?? []) as unknown as FullNurseSchema[]}
          preceptorList={preceptorList}
          fetchNurseSchema={fetchNurseSchema}
          setIsOpendExcelUpload={setIsOpendExcelUpload}
        />
      )}

      {/* 사이드 프로필 모달 */}
      {isSideProfileOpend && (
        <NurseEditSideProfile
          isOpen={isSideProfileOpend}
          onClose={() => {
            setIsSideProfileOpend(false);
            setActiveNurseIdForSideProfile(null);
          }}
          selectedGroupId={selectedGroupId}
          isAdmin={isAdmin}
          nurseId={activeNurseIdForSideProfile}
          allNurses={nurseDatasWithPrecepteePeriods}
          disableShiftAndWantedEdit={isExternalNurseForSideProfile}
          year={selectedYear}
          month={selectedMonth}
          teamsSchema={teamsSchema}
          gradeData={gradeData}
          shiftsData={shiftsData}
          rosterConfig={rosterConfig}
          monthMembers={monthMembers}
        />
      )}

      {/* 그룹 수정 */}
      {openAddGroup && (
        <AddGroupModal
          onClose={() => setOpenAddGroup(false)}
          onCreated={async (g) => {
            setOpenAddGroup(false);
            await fetchGroups();
            setSelectedGroupId(g.group_id);
          }}
          fetchGroups={fetchGroups}
        />
      )}

      {/* 엑셀 업로드 */}
      {isOpendExcelUpload2 && (
        <MgmtExcelUpload2
          onClose={() => setIsOpendExcelUpload2(false)}
          isMasterAdmin={Boolean(loginUser?.is_master_admin)}
          selectedGroupId={selectedGroupId}
          fetchNurseSchema={(gid?: string) => fetchNurseSchema(gid)}
        />
      )}

      {/* Grade 설정 */}
      {isGradeModalOpen && (
        <GradeSettingModal
          onClose={() => setIsGradeModalOpen(false)}
          isAdmin={isAdmin}
          selectedGroupId={selectedGroupId}
          nurseDatas={nurseDatas}
        />
      )}

      {/* 팀설정 */}
      {isTeamModalOpen && (
        <TeamSettingModal
          onClose={() => setIsTeamModalOpen(false)}
          selectedGroupId={isAdmin ? selectedGroupId : ""}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          memberStatusMap={memberStatusMap}
          nurseDatas={nurseDatas}
        />
      )}

      {/* 병동 간 재분배 (옵션2) */}
      {isRedistributeOpen && (
        <WardRedistributeModal
          onClose={() => setIsRedistributeOpen(false)}
          groups={(groups ?? []).map((g) => ({
            group_id: g.group_id,
            group_name: g.group_name,
          }))}
        />
      )}

      {/* 근무자 활성 */}
      {isNurseModalOpen && (
        <NurseActiveSwitch
          onClose={() => setIsNurseModalOpen(false)}
          selectedGroupId={selectedGroupId}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          nurseData={nurseDatasWithPrecepteePeriods}
          precepteePeriods={precepteePeriods}
          shiftsData={shiftsData}
          isUseMid={rosterConfig?.use_mid ?? false}
        />
      )}

      {/* 근무자 추가 */}
      {isNurseAddModalOpen && (
        <NurseAddModal
          onClose={() => setIsNurseAddModalOpen(false)}
          selectedGroupId={selectedGroupId}
        />
      )}
    </div>
  );
}
export default Head_nurse_management;
